/**
 * Small UI kit. iPad-first: generous touch targets (≥ 44 pt), large type, clear contrast.
 * Colours come from CSS variables in styles.css so light/dark both work.
 */
import { LoaderCircle, X, type LucideIcon } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';
import { isTopOverlay, pushOverlay, removeOverlay, trapTarget } from './overlayStack';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'gold';
type Size = 'sm' | 'md' | 'lg';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-[var(--accent)] text-[var(--accent-ink)] hover:brightness-110 active:brightness-95',
  secondary:
    'bg-[var(--surface)] text-[var(--ink)] border border-[var(--line)] hover:bg-[var(--surface-2)] active:bg-[var(--accent-soft)]',
  ghost: 'bg-transparent text-[var(--accent)] hover:bg-[var(--accent-soft)]',
  danger: 'bg-[var(--danger)] text-white hover:brightness-110',
  gold: 'bg-gold-500 text-navy-900 hover:bg-gold-400',
};
const SIZE: Record<Size, string> = {
  sm: 'min-h-9 px-3 text-[15px] gap-1.5 rounded-lg',
  md: 'min-h-11 px-4 text-[17px] gap-2 rounded-xl',
  lg: 'min-h-14 px-6 text-[19px] gap-2.5 rounded-2xl',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  busy?: boolean;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon: Icon, iconRight: IconR, busy, block, className, children, disabled, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || busy}
      className={cx(
        'inline-flex select-none items-center justify-center font-semibold transition disabled:cursor-not-allowed disabled:opacity-50',
        VARIANT[variant],
        SIZE[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {busy ? <LoaderCircle className="size-5 animate-spin" aria-hidden /> : Icon ? <Icon className="size-5 shrink-0" aria-hidden /> : null}
      {children}
      {IconR && !busy ? <IconR className="size-5 shrink-0" aria-hidden /> : null}
    </button>
  );
});

export function IconButton({
  icon: Icon,
  label,
  className,
  active,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { icon: LucideIcon; label: string; active?: boolean }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-xl transition',
        active ? 'bg-[var(--accent)] text-[var(--accent-ink)]' : 'text-[var(--ink-2)] hover:bg-[var(--accent-soft)]',
        className,
      )}
      {...rest}
    >
      <Icon className="size-6" aria-hidden />
    </button>
  );
}

export function Card({ className, children, as: As = 'div' }: { className?: string; children: ReactNode; as?: 'div' | 'section' | 'article' | 'li' }) {
  return (
    <As className={cx('rounded-[var(--radius-card)] border border-[var(--line)] bg-[var(--surface)] shadow-[var(--shadow-card)]', className)}>
      {children}
    </As>
  );
}

export function Spinner({ className, label }: { className?: string; label?: string }) {
  return (
    <span role="status" className={cx('inline-flex items-center gap-2 text-[var(--muted)]', className)}>
      <LoaderCircle className="size-6 animate-spin" aria-hidden />
      {label ? <span>{label}</span> : <span className="sr-only">…</span>}
    </span>
  );
}

export function CenteredSpinner({ label }: { label?: string }) {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner label={label} />
    </div>
  );
}

export function EmptyState({ icon: Icon, title, children, action }: { icon?: LucideIcon; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      {Icon ? (
        <div className="flex size-16 items-center justify-center rounded-full bg-[var(--accent-soft)] text-[var(--accent)]">
          <Icon className="size-8" aria-hidden />
        </div>
      ) : null}
      <h3 className="text-xl font-semibold">{title}</h3>
      {children ? <div className="max-w-md text-[var(--muted)]">{children}</div> : null}
      {action}
    </div>
  );
}

export function ErrorBox({ children, onRetry, retryLabel }: { children: ReactNode; onRetry?: () => void; retryLabel?: string }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-red-300/60 bg-red-50 px-4 py-3 text-red-900 dark:bg-red-950/40 dark:text-red-200">
      <span className="flex-1">{children}</span>
      {onRetry ? (
        <Button size="sm" variant="secondary" onClick={onRetry}>
          {retryLabel ?? '↻'}
        </Button>
      ) : null}
    </div>
  );
}

/* ─── Form controls ─────────────────────────────────────────────────────── */

const fieldBase =
  'w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-4 py-3 text-[17px] text-[var(--ink)] placeholder:text-[var(--muted)] focus:border-[var(--focus)] focus:outline-none focus:ring-2 focus:ring-[var(--focus)]/30 disabled:opacity-60';

export function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: ReactNode; error?: string | null; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-[15px] font-semibold text-[var(--ink-2)]">
        {label}
      </label>
      {children}
      {error ? <p className="text-[15px] text-[var(--danger-text)]">{error}</p> : hint ? <p className="text-[14px] text-[var(--muted)]">{hint}</p> : null}
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { label?: string; hint?: ReactNode; error?: string | null }>(
  function TextInput({ label, hint, error, className, id, ...rest }, ref) {
    const autoId = useId();
    const input = <input ref={ref} id={id ?? autoId} className={cx(fieldBase, className)} {...rest} />;
    return label ? (
      <Field label={label} hint={hint} error={error} htmlFor={id ?? autoId}>
        {input}
      </Field>
    ) : (
      input
    );
  },
);

export function TextArea({ label, hint, error, className, id, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; hint?: ReactNode; error?: string | null }) {
  const autoId = useId();
  const el = <textarea id={id ?? autoId} className={cx(fieldBase, 'min-h-28 resize-y', className)} {...rest} />;
  return label ? (
    <Field label={label} hint={hint} error={error} htmlFor={id ?? autoId}>
      {el}
    </Field>
  ) : (
    el
  );
}

export function Select({ label, hint, className, id, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { label?: string; hint?: ReactNode }) {
  const autoId = useId();
  const el = (
    <select id={id ?? autoId} className={cx(fieldBase, 'appearance-none bg-[length:20px] pr-10', className)} {...rest}>
      {children}
    </select>
  );
  return label ? (
    <Field label={label} hint={hint} htmlFor={id ?? autoId}>
      {el}
    </Field>
  ) : (
    el
  );
}

export function Switch({ checked, onChange, label, description, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; description?: ReactNode; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="flex items-center gap-4 py-2">
      <div className="flex-1">
        <label htmlFor={id} className="font-medium">
          {label}
        </label>
        {description ? <p className="text-[15px] text-[var(--muted)]">{description}</p> : null}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className="group relative flex h-11 w-16 shrink-0 items-center justify-center disabled:opacity-50"
      >
        {/* 44 pt hit area around the 32 pt track */}
        <span className={cx('relative h-8 w-14 rounded-full transition', checked ? 'bg-[var(--accent)]' : 'bg-[var(--line)]')}>
          <span className={cx('absolute top-1 size-6 rounded-full bg-white shadow transition-all', checked ? 'left-7' : 'left-1')} />
        </span>
      </button>
    </div>
  );
}

/** Multi-select chips (e.g. audience picker). */
export function ChipGroup<V extends string>({
  options,
  value,
  onChange,
  label,
  single,
}: {
  options: { value: V; label: string; color?: string; disabled?: boolean; hint?: string }[];
  value: V[];
  onChange: (v: V[]) => void;
  label?: string;
  single?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      {label ? <span className="text-[15px] font-semibold text-[var(--ink-2)]">{label}</span> : null}
      <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
        {options.map((o) => {
          const on = value.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              disabled={o.disabled}
              title={o.hint}
              onClick={() => onChange(single ? [o.value] : on ? value.filter((x) => x !== o.value) : [...value, o.value])}
              className={cx(
                'min-h-11 rounded-full border px-4 text-[16px] font-medium transition disabled:opacity-40',
                on ? 'border-transparent bg-[var(--accent)] text-[var(--accent-ink)]' : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink-2)]',
              )}
              style={on && o.color ? { background: o.color, color: '#fff' } : undefined}
            >
              {on ? '✓ ' : ''}
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Overlays ──────────────────────────────────────────────────────────── */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.getClientRects().length > 0 && !el.closest('[inert]'));
}

/**
 * Bottom sheet on phones, centred panel on wide screens. A modal dialog: named by its title, focus
 * moves into it on open, Tab stays inside, and focus returns to the opener on close. Sheets without
 * a footer get a header close button (the backdrop is not reachable with VoiceOver).
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const t = useT();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });
  // The opener is read while rendering the opening: by the time effects run, an autoFocus field
  // inside the sheet already has focus.
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  if (open && !wasOpenRef.current && typeof document !== 'undefined') {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  wasOpenRef.current = open;

  // Keyed on `open` only (callers often pass an inline onClose): the stack order stays the open order.
  useEffect(() => {
    if (!open) return;
    const token = pushOverlay();
    const panel = panelRef.current;
    const opener = openerRef.current;
    // Children may have focused a field themselves (autoFocus); otherwise focus the dialog.
    if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      // Only the top sheet reacts, so stacked sheets close one at a time. preventDefault marks the
      // key as handled for the other window listeners (page keys, annotation keys check it).
      if (!isTopOverlay(token) || e.defaultPrevented || !panel) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
      } else if (e.key === 'Tab') {
        const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const target = trapTarget(focusables(panel), active, {
          inside: active !== null && panel.contains(active),
          onPanel: active === panel,
          shift: e.shiftKey,
        });
        if (target === null) return;
        e.preventDefault();
        (target === 'panel' ? panel : target).focus({ preventScroll: target === 'panel' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      removeOverlay(token);
      // Return focus to the opener unless the user has already moved it somewhere else.
      const active = document.activeElement;
      const focusLost = !active || active === document.body || (panel?.contains(active) ?? false);
      if (focusLost && opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;
  const showClose = !footer;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : t('common.dialog')}
        tabIndex={-1}
        className={cx(
          'relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-3xl bg-[var(--surface)] shadow-[var(--shadow-sheet)] outline-none md:rounded-3xl',
          wide ? 'md:max-w-3xl' : 'md:max-w-lg',
        )}
        style={{ animation: 'kgr-sheet-in 180ms ease-out', paddingBottom: 'var(--safe-bottom)' }}
      >
        <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-[var(--line)] md:hidden" aria-hidden />
        {title || showClose ? (
          <div className={cx('relative flex min-h-12 items-center justify-center pt-4 pb-2', showClose ? 'px-16' : 'px-6')}>
            {title ? (
              <div id={titleId} className="text-center text-xl font-semibold">
                {title}
              </div>
            ) : null}
            {showClose ? (
              <button
                type="button"
                onClick={onClose}
                aria-label={t('common.close')}
                title={t('common.close')}
                className="absolute top-2 right-3 inline-flex size-11 items-center justify-center rounded-full text-[var(--ink-2)] hover:bg-[var(--accent-soft)]"
              >
                <X className="size-6" aria-hidden />
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="scroll-y flex-1 px-6 pb-4">{children}</div>
        {footer ? <div className="flex gap-3 border-t border-[var(--line)] px-6 py-4">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmSheet({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  danger,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" block onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} block busy={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body ? <div className="py-2 text-center text-[var(--ink-2)]">{body}</div> : null}
    </Sheet>
  );
}
